import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { 
  MapPin, Calendar, Package, Send, HelpCircle, X, Shield, Clock,
  Layers, Box, Weight, Ruler, AlertCircle, Truck, DollarSign, Share2
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { Message, Load, LoadItem } from "@shared/schema";
import MemberLink from "./MemberLink";

interface PublicUserInfo {
  id: string;
  company: string;
  companyLogo?: string | null;
  dotNumber?: string;
  mcNumber?: string;
  adminVerified: boolean;
  upvoteCount: number;
  completedShipments: number;
  userType: string;
}

interface LoadWithPoster extends Load {
  poster: PublicUserInfo | null;
  items?: LoadItem[];
}

interface LoadShareCardProps {
  message: Message;
  isOwn: boolean;
  currentUserId?: string;
  onConversationUpdate?: () => void;
}

function FreightItemDetail({ item, index }: { item: LoadItem; index: number }) {
  const getItemIcon = (unit: string) => {
    switch (unit) {
      case "Pallet": return <Layers className="w-4 h-4" />;
      case "Crate": return <Box className="w-4 h-4" />;
      default: return <Package className="w-4 h-4" />;
    }
  };

  return (
    <div className="p-2 bg-muted/30 rounded" data-testid={`freight-item-${index}`}>
      <div className="flex items-center gap-2">
        {getItemIcon(item.handlingUnit || "Other")}
        <span className="text-sm font-medium">{item.handlingUnit || "Item"}</span>
        <Badge variant="secondary" className="text-xs">Qty: {item.quantity || 1}</Badge>
      </div>
      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
        {(item.length || item.width || item.height) && (
          <span className="flex items-center gap-1">
            <Ruler className="w-3 h-3" />
            {item.length || 0}" × {item.width || 0}" × {item.height || 0}"
          </span>
        )}
        <span className="flex items-center gap-1">
          <Weight className="w-3 h-3" />
          {((item.weight || 0) * (item.quantity || 1)).toLocaleString()} lbs
        </span>
      </div>
    </div>
  );
}

export default function LoadShareCard({ 
  message, 
  isOwn,
  currentUserId,
  onConversationUpdate
}: LoadShareCardProps) {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [quotePrice, setQuotePrice] = useState("");
  const [quoteMessage, setQuoteMessage] = useState("");
  const [questionText, setQuestionText] = useState("");

  const loadId = message.loadId;

  // Fetch full load details - using custom queryFn for dynamic URL pattern
  // This follows the same pattern as ShareLoadDialog for dynamic ID-based routes
  const { data: loadData, isLoading: isLoadingLoad, error: loadError } = useQuery<LoadWithPoster>({
    queryKey: ["/api/loads", loadId],
    queryFn: async () => {
      const response = await fetch(`/api/loads/${loadId}`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error("Failed to fetch load");
      return response.json();
    },
    enabled: !!loadId && !isOwn,
  });

  const totalWeight = loadData?.items?.reduce((sum, item) => 
    sum + ((item.weight || 0) * (item.quantity || 1)), 0) || loadData?.weight || 0;
  const totalItems = loadData?.items?.reduce((sum, item) => sum + (item.quantity || 1), 0) || 1;
  
  const hasStackableItems = loadData?.items?.some(item => item.stackable) || false;
  const hasHazardousItems = loadData?.items?.some(item => item.hazardous) || false;

  const generateQuoteTemplate = () => {
    if (!loadData) return "";
    const pickupDate = loadData.pickupDate ? new Date(loadData.pickupDate).toLocaleDateString() : "TBD";
    const deliveryDate = loadData.deliveryDate ? new Date(loadData.deliveryDate).toLocaleDateString() : null;
    const loadRef = loadData.loadIdentifier ? `Load ${loadData.loadIdentifier}` : 'this load';
    
    return `Hi,

I'm interested in hauling ${loadRef} and would like to submit a quote.

Load Summary:
${loadData.loadIdentifier ? `• Load ID: ${loadData.loadIdentifier}\n` : ''}• Route: ${loadData.origin} → ${loadData.destination}
• Weight: ${totalWeight.toLocaleString()} lbs (${totalItems} item${totalItems > 1 ? 's' : ''})
• Pickup: ${pickupDate}
${deliveryDate ? `• Delivery: ${deliveryDate}\n` : ""}

My Quote: $${quotePrice || "[Enter your price]"}

I can meet your pickup and delivery requirements. Please let me know if you'd like to proceed or if you have any questions.

Thank you!`;
  };

  const submitQuoteMutation = useMutation({
    mutationFn: async (data: { toUserId: string; loadId: string; price: string; message: string }) => {
      const response = await apiRequest("POST", "/api/messages/quote-submission", data);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Quote Submitted",
        description: "Your quote has been sent to the shipper.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/messages"] });
      setShowQuoteForm(false);
      setQuotePrice("");
      setQuoteMessage("");
      onConversationUpdate?.();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const askQuestionMutation = useMutation({
    mutationFn: async (data: { toUserId: string; loadId: string; question: string }) => {
      const response = await apiRequest("POST", "/api/messages/ask-question", data);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Question Sent",
        description: "Your question has been sent. Continue the conversation below.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/messages"] });
      setShowQuestionForm(false);
      setQuestionText("");
      onConversationUpdate?.();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const notInterestedMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/messages/decline-load-share", {
        loadId,
        shipperId: message.fromUserId,
      });
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Response Sent",
        description: "The shipper has been notified that you're not interested in this load.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/messages"] });
      onConversationUpdate?.();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmitQuote = () => {
    if (!quotePrice || !loadData?.userId) {
      toast({
        title: "Error",
        description: "Please enter a quote price",
        variant: "destructive",
      });
      return;
    }

    const msg = quoteMessage || generateQuoteTemplate();
    
    submitQuoteMutation.mutate({
      toUserId: loadData.userId,
      loadId: loadData.id,
      price: quotePrice,
      message: msg,
    });
  };

  const handleStartQuote = () => {
    setQuoteMessage(generateQuoteTemplate());
    setShowQuoteForm(true);
    setShowQuestionForm(false);
  };

  const handleAskQuestion = () => {
    if (!questionText.trim() || !loadData?.userId) {
      toast({
        title: "Error",
        description: "Please enter your question",
        variant: "destructive",
      });
      return;
    }

    askQuestionMutation.mutate({
      toUserId: loadData.userId,
      loadId: loadData.id,
      question: questionText.trim(),
    });
  };

  const formatTime = (dateString: Date) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", { 
      month: "short", 
      day: "numeric", 
      hour: "numeric", 
      minute: "2-digit" 
    });
  };

  // For the sender (shipper), just show a simple confirmation message
  if (isOwn) {
    return (
      <div className="flex justify-end mb-4">
        <div className="max-w-[80%] border-2 border-primary/40 bg-primary/5 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Package className="w-5 h-5 text-primary" />
            <span className="font-semibold text-primary">Load Shared</span>
          </div>
          <p className="text-sm whitespace-pre-line" data-testid={`text-message-${message.id}`}>
            {message.message}
          </p>
          <p className="text-xs mt-2 text-muted-foreground">{formatTime(message.createdAt)}</p>
        </div>
      </div>
    );
  }

  // For the carrier recipient, show full load details with action buttons
  return (
    <div className="flex flex-col items-start mb-4 gap-2">
      {/* Introductory context message - styled like other templated messages */}
      <div className="max-w-[80%] border-2 border-amber-500 bg-amber-50 dark:bg-amber-950/30 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <Share2 className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          <span className="font-semibold text-amber-600 dark:text-amber-400">Load Shared With You</span>
        </div>
        <p className="text-sm">
          This load was shared with you based on the truck space you posted. Review the details below and let the shipper know if you're interested.
        </p>
        <p className="text-xs mt-2 text-muted-foreground">{formatTime(message.createdAt)}</p>
      </div>
      
      {/* Load details card */}
      <Card className="max-w-[90%] border-2 border-primary/40 p-4" data-testid={`load-share-card-${message.id}`}>
        {/* Header */}
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <Truck className="w-5 h-5 text-primary" />
            <span className="font-semibold text-primary">Load Details</span>
          </div>
          {loadData?.loadIdentifier && (
            <Badge variant="outline" className="font-mono text-sm" data-testid="text-load-identifier">
              {loadData.loadIdentifier}
            </Badge>
          )}
        </div>

        {isLoadingLoad ? (
          <div className="py-4 text-center text-muted-foreground">Loading load details...</div>
        ) : loadError ? (
          <div className="py-4 text-center">
            <p className="text-destructive mb-2">Unable to load full details</p>
            <p className="text-sm text-muted-foreground whitespace-pre-line">{message.message}</p>
          </div>
        ) : loadData ? (
          <div className="space-y-4">
            {/* Shipper Info */}
            {loadData.poster && (
              <div className="flex items-center gap-3 p-2 bg-muted/50 rounded-lg">
                <div className="flex-1">
                  <MemberLink 
                    userId={loadData.poster.id} 
                    name={loadData.poster.company}
                    companyLogo={loadData.poster.companyLogo}
                    showAvatar
                    avatarSize="sm"
                    className="font-medium text-sm"
                    data-testid="link-shipper-company"
                  />
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                    <Badge variant="secondary" className="text-xs">{loadData.poster.userType}</Badge>
                    {loadData.poster.adminVerified && (
                      <Badge variant="outline" className="text-green-600 border-green-600 text-xs">
                        <Shield className="w-3 h-3 mr-1" />
                        Verified
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Route */}
            <div className="space-y-2">
              <h4 className="text-sm font-semibold flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Route
              </h4>
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2 border rounded">
                  <div className="flex items-center gap-1 text-green-600 text-xs mb-1">
                    <MapPin className="w-3 h-3" />
                    <span>Pickup</span>
                  </div>
                  <p className="text-sm font-medium" data-testid="text-origin">{loadData.origin}</p>
                </div>
                <div className="p-2 border rounded">
                  <div className="flex items-center gap-1 text-red-600 text-xs mb-1">
                    <MapPin className="w-3 h-3" />
                    <span>Delivery</span>
                  </div>
                  <p className="text-sm font-medium" data-testid="text-destination">{loadData.destination}</p>
                </div>
              </div>
            </div>

            {/* Schedule */}
            <div className="space-y-2">
              <h4 className="text-sm font-semibold flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Schedule
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Pickup Date</p>
                    <p className="text-sm font-medium" data-testid="text-pickup-date">
                      {loadData.pickupDate ? new Date(loadData.pickupDate).toLocaleDateString() : "Flexible"}
                    </p>
                  </div>
                </div>
                {loadData.deliveryDate && (
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Delivery Date</p>
                      <p className="text-sm font-medium" data-testid="text-delivery-date">
                        {new Date(loadData.deliveryDate).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Freight Items */}
            <div className="space-y-2">
              <h4 className="text-sm font-semibold flex items-center gap-2">
                <Package className="w-4 h-4" />
                Freight ({totalItems} item{totalItems > 1 ? 's' : ''}, {totalWeight.toLocaleString()} lbs total)
              </h4>
              <div className="space-y-1">
                {loadData.items && loadData.items.length > 0 ? (
                  loadData.items.map((item, idx) => (
                    <FreightItemDetail key={idx} item={item} index={idx} />
                  ))
                ) : (
                  <div className="p-2 bg-muted/30 rounded">
                    <div className="flex items-center gap-2 text-sm">
                      <Package className="w-4 h-4" />
                      <span>{loadData.handlingUnit || "Standard Freight"}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {loadData.weight?.toLocaleString()} lbs
                      {loadData.dimensions && ` • ${loadData.dimensions}`}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Special Information */}
            {(loadData.liftgatePickup || loadData.insidePickup || loadData.liftgateDelivery || 
              loadData.insideDelivery || loadData.deliveryAppointmentRequired || loadData.callBeforeDelivery ||
              hasStackableItems || hasHazardousItems) && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold">Special Information</h4>
                <div className="flex flex-wrap gap-1">
                  {hasHazardousItems && (
                    <Badge variant="outline" className="text-yellow-600 border-yellow-600 text-xs">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      Hazardous
                    </Badge>
                  )}
                  {hasStackableItems && (
                    <Badge variant="outline" className="text-green-600 border-green-600 text-xs">
                      Stackable
                    </Badge>
                  )}
                  {loadData.liftgatePickup && <Badge variant="secondary" className="text-xs">Liftgate Pickup</Badge>}
                  {loadData.insidePickup && <Badge variant="secondary" className="text-xs">Inside Pickup</Badge>}
                  {loadData.liftgateDelivery && <Badge variant="secondary" className="text-xs">Liftgate Delivery</Badge>}
                  {loadData.insideDelivery && <Badge variant="secondary" className="text-xs">Inside Delivery</Badge>}
                  {loadData.deliveryAppointmentRequired && <Badge variant="secondary" className="text-xs">Appointment Required</Badge>}
                  {loadData.callBeforeDelivery && <Badge variant="secondary" className="text-xs">Call Before</Badge>}
                </div>
              </div>
            )}

            {/* Shipper Notes */}
            {loadData.details && (
              <div className="space-y-1">
                <h4 className="text-sm font-semibold">Shipper Notes</h4>
                <p className="text-xs text-muted-foreground bg-muted/30 p-2 rounded" data-testid="text-details">
                  {loadData.details}
                </p>
              </div>
            )}

            <Separator />

            {/* Quote Form */}
            {showQuoteForm && (
              <div className="space-y-3 p-3 bg-muted/30 rounded-lg">
                <h4 className="font-semibold flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Submit Your Quote
                </h4>
                <div className="space-y-2">
                  <Label htmlFor="quote-price" className="text-sm">Your Quote Price ($)</Label>
                  <Input
                    id="quote-price"
                    type="number"
                    placeholder="Enter amount"
                    value={quotePrice}
                    onChange={(e) => {
                      setQuotePrice(e.target.value);
                      setQuoteMessage(generateQuoteTemplate());
                    }}
                    data-testid="input-quote-price"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="quote-message" className="text-sm">Message (optional)</Label>
                  <Textarea
                    id="quote-message"
                    rows={4}
                    value={quoteMessage}
                    onChange={(e) => setQuoteMessage(e.target.value)}
                    placeholder="Add any additional details..."
                    data-testid="textarea-quote-message"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleSubmitQuote}
                    disabled={submitQuoteMutation.isPending || !quotePrice}
                    className="flex-1"
                    data-testid="button-submit-quote"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {submitQuoteMutation.isPending ? "Sending..." : "Submit Quote"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowQuoteForm(false);
                      setQuotePrice("");
                      setQuoteMessage("");
                    }}
                    data-testid="button-cancel-quote"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Question Form */}
            {showQuestionForm && (
              <div className="space-y-3 p-3 bg-muted/30 rounded-lg">
                <h4 className="font-semibold flex items-center gap-2">
                  <HelpCircle className="w-4 h-4" />
                  Ask a Question
                </h4>
                <div className="space-y-2">
                  <Label htmlFor="question-text" className="text-sm">Your Question</Label>
                  <Textarea
                    id="question-text"
                    rows={3}
                    value={questionText}
                    onChange={(e) => setQuestionText(e.target.value)}
                    placeholder="Ask about pickup times, freight details, etc..."
                    data-testid="textarea-question"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleAskQuestion}
                    disabled={askQuestionMutation.isPending || !questionText.trim()}
                    className="flex-1"
                    data-testid="button-send-question"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {askQuestionMutation.isPending ? "Sending..." : "Send Question"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowQuestionForm(false);
                      setQuestionText("");
                    }}
                    data-testid="button-cancel-question"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            {!showQuoteForm && !showQuestionForm && (
              <div className="flex flex-wrap gap-2 pt-2">
                <Button
                  onClick={handleStartQuote}
                  className="flex-1"
                  data-testid="button-submit-quote-action"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Submit Quote
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowQuestionForm(true);
                    setShowQuoteForm(false);
                  }}
                  className="flex-1"
                  data-testid="button-ask-question-action"
                >
                  <HelpCircle className="w-4 h-4 mr-2" />
                  Ask a Question
                </Button>
                <Button
                  variant="outline"
                  onClick={() => notInterestedMutation.mutate()}
                  disabled={notInterestedMutation.isPending}
                  data-testid="button-not-interested"
                >
                  <X className="w-4 h-4 mr-2" />
                  {notInterestedMutation.isPending ? "Sending..." : "Not Interested"}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="py-4 text-center text-muted-foreground">
            Load details not available
          </div>
        )}

        <p className="text-xs mt-3 text-muted-foreground">{formatTime(message.createdAt)}</p>
      </Card>
    </div>
  );
}
